import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Shield, Facebook, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";

const socialLinks = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Linkedin, href: "#", label: "LinkedIn" }
];

export default function Footer() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const l = lang || 'lv';

  const footerLinks = {
    pakalpojumi: [
      { label: t("footer.careCenters"), href: "#pakalpojumi" },
      { label: t("footer.socialServices"), href: "#pakalpojumi" }
    ],
    uzņēmums: [
      { label: t("nav.aboutUs"), href: "#par-mums" },
      { label: t("nav.howItWorks"), href: "#ka-tas-darbojas" },
      { label: t("nav.benefits"), href: "#priekšrocības" },
      { label: t("nav.contacts"), href: "#kontakti" }
    ],
    atbalsts: [
      { label: t("footer.faq"), href: "#buj" },
      { label: t("footer.techSupport"), href: "#kontakti" },
      { label: t("footer.privacyPolicy"), href: `/${l}/privatuma-politika`, isRoute: true }
    ]
  };

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-foreground text-white" data-testid="footer" role="contentinfo">
      <div className="section-container py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="col-span-2 md:col-span-4 lg:col-span-2 mb-8 lg:mb-0">
            <a href={`/${l}`} className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <span className="text-xl font-bold text-white block" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("nav.brand")}</span>
                <span className="text-xs text-white/60">{t("nav.brandSub")}</span>
              </div>
            </a>
            <p className="text-white/70 mb-6 max-w-sm">{t("footer.description")}</p>
            
            <div className="space-y-2 mb-6">
              <p className="text-white/90">
                <span className="text-white/60">{t("footer.phone")}</span>{" "}
                <a href="tel:+37129173338" className="hover:text-primary">+371 29173338</a>
                <span className="text-white/50 text-sm ml-2">Agnese Bērziņa</span>
              </p>
              <p className="text-white/90">
                <span className="text-white/60">{t("footer.phone")}</span>{" "}
                <a href="tel:+37125775533" className="hover:text-primary">+371 25775533</a>
                <span className="text-white/50 text-sm ml-2">Didzis Tupureins</span>
              </p>
            </div>
            
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a key={index} href={social.href} aria-label={social.label} className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-primary transition-colors">
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("footer.servicesTitle")}</h4>
            <ul className="space-y-3">
              {footerLinks.pakalpojumi.map((link, index) => (
                <li key={index}>
                  <button onClick={() => scrollToSection(link.href)} className="text-white/70 hover:text-white transition-colors text-left">{link.label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("footer.infoTitle")}</h4>
            <ul className="space-y-3">
              {footerLinks.uzņēmums.map((link, index) => (
                <li key={index}>
                  <button onClick={() => scrollToSection(link.href)} className="text-white/70 hover:text-white transition-colors text-left">{link.label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{t("footer.supportTitle")}</h4>
            <ul className="space-y-3">
              {footerLinks.atbalsts.map((link, index) => (
                <li key={index}>
                  {link.isRoute ? (
                    <Link to={link.href} className="text-white/70 hover:text-white transition-colors">{link.label}</Link>
                  ) : (
                    <button onClick={() => scrollToSection(link.href)} className="text-white/70 hover:text-white transition-colors text-left">{link.label}</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">{t("footer.copyright", { year: new Date().getFullYear() })}</p>
            <div className="flex gap-6">
              <Link to={`/${l}/privatuma-politika`} className="text-white/60 hover:text-white text-sm transition-colors">{t("footer.privacyPolicy")}</Link>
              <button className="text-white/60 hover:text-white text-sm transition-colors">{t("footer.terms")}</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
